/**
 * Passcode Gate — Night Suite reception. Devanagari mark sits centered with
 * a slowly rotating moon-phase ring behind it. Single passcode input.
 */
import { store } from '../store.ts';
import { decryptJSON } from '../crypto.ts';
import { saveSummary, loadSummary } from '../storage.ts';
import type { SleepSummary } from '../types.ts';

const TPL = /* html */ `
<style>
  :host {
    display: grid;
    place-items: center;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 50% at 50% 30%, oklch(20% 0.06 268 / 0.7), transparent 65%),
      var(--paper);
    position: relative;
    overflow: hidden;
    container-type: inline-size;
  }
  :host::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.7 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    background-size: 320px 320px;
    mix-blend-mode: screen;
    opacity: 0.04;
    pointer-events: none;
  }
  .wrap {
    text-align: center;
    display: grid;
    gap: 24px;
    padding: 36px;
    max-width: 440px;
    position: relative;
    z-index: 2;
  }
  .mark-stage {
    position: relative;
    display: grid;
    place-items: center;
    isolation: isolate;
  }
  .moon-ring {
    position: absolute;
    width: clamp(220px, 60cqw, 320px);
    aspect-ratio: 1 / 1;
    border-radius: 50%;
    background:
      conic-gradient(from -90deg,
        oklch(91% 0.04 80 / 0.32) 0deg,
        oklch(91% 0.04 80 / 0.32) 175deg,
        oklch(7% 0.02 268 / 0.85) 175deg,
        oklch(7% 0.02 268 / 0.85) 360deg);
    -webkit-mask: radial-gradient(circle, transparent 85px, black 86px, black 105px, transparent 106px);
            mask: radial-gradient(circle, transparent 85px, black 86px, black 105px, transparent 106px);
    filter: blur(1px);
    opacity: 0.7;
    animation: orbit 60s linear infinite;
    z-index: -1;
  }
  .mark {
    font-family: var(--hindi);
    font-size: clamp(72px, 18vw, 140px);
    font-weight: 400;
    color: var(--moon);
    line-height: 0.9;
    letter-spacing: -0.02em;
    text-wrap: balance;
    text-shadow: 0 0 30px oklch(86% 0.02 248 / 0.35);
    position: relative;
    z-index: 1;
  }
  .en {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: var(--ink-faint);
    margin-top: 4px;
  }
  .tag {
    font-family: var(--display);
    font-size: 22px;
    font-style: italic;
    color: var(--moon-cream);
    font-weight: 400;
    text-wrap: pretty;
    margin-block: 8px 4px;
    text-shadow: 0 0 16px oklch(91% 0.04 80 / 0.2);
  }
  .tag-trans {
    font-family: var(--display);
    font-size: 13px;
    font-style: italic;
    color: var(--ink-faint);
    font-weight: 400;
    text-wrap: pretty;
    margin-bottom: 12px;
    letter-spacing: 0.005em;
  }
  .field { display: grid; gap: 10px; }
  input {
    font-family: var(--mono);
    font-size: 18px;
    text-align: center;
    padding: 18px;
    background: var(--paper-up);
    border: 1px solid var(--rule-strong);
    border-radius: 6px;
    color: var(--ink);
    outline: none;
    letter-spacing: 0.4em;
    transition: border-color 200ms var(--ease-suite), box-shadow 200ms var(--ease-suite);
  }
  input:focus {
    border-color: var(--moon);
    box-shadow: 0 0 0 3px oklch(86% 0.02 248 / 0.12), inset 0 0 20px oklch(86% 0.02 248 / 0.06);
  }
  input::placeholder { color: var(--ink-faint); letter-spacing: 0.2em; }
  button {
    font-family: var(--mono);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.32em;
    padding: 18px;
    background: var(--moon);
    color: var(--paper);
    border-radius: 6px;
    font-weight: 700;
    transition: transform 140ms var(--ease-spring), filter 160ms;
  }
  button:hover { filter: brightness(1.08); }
  button:active { transform: scale(0.97); filter: brightness(0.94); }
  .hint {
    font-family: var(--mono);
    font-size: 10px;
    color: var(--ink-faint);
    letter-spacing: 0.05em;
    margin-top: 4px;
  }
  .err {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--rose);
    letter-spacing: 0.05em;
    min-height: 14px;
  }
  @keyframes orbit {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @media (prefers-reduced-motion: reduce) {
    .moon-ring { animation: none; }
  }
</style>
<div class="wrap">
  <div class="mark-stage">
    <div class="moon-ring" aria-hidden="true"></div>
    <div>
      <div class="mark">आरामगाह</div>
      <div class="en">Aramgah</div>
    </div>
  </div>
  <div class="tag">"Raat ki kahaani. Subah ka qissa."</div>
  <div class="tag-trans">The night's tale, the morning's account.</div>
  <form class="field" id="form">
    <input id="pass" type="password" placeholder="passcode" autocomplete="off" autocapitalize="off" autocorrect="off" inputmode="text" />
    <button type="submit" id="btn">Open the suite</button>
    <div class="err" id="err"></div>
  </form>
  <div class="hint">Issue I · MMXXVI · decryption stays on your device</div>
</div>
`;

export class AramgahGate extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const form = this.#shadow.getElementById('form') as HTMLFormElement;
    const pass = this.#shadow.getElementById('pass') as HTMLInputElement;
    const err = this.#shadow.getElementById('err') as HTMLDivElement;

    queueMicrotask(() => pass.focus());

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      err.textContent = '';
      const passcode = pass.value.trim();
      if (!passcode) return;
      try {
        const summary = await this.#unlock(passcode);
        await saveSummary(summary);
        sessionStorage.setItem('aramgah:passcode', passcode);
        this.#transitionToApp(summary);
      } catch {
        err.textContent = 'galat passcode';
        pass.value = '';
        pass.focus();
      }
    });
  }

  async #unlock(passcode: string): Promise<SleepSummary> {
    const cached = await loadSummary();
    if (cached) return cached;
    const res = await fetch('/data.encrypted.json', { cache: 'no-cache' });
    if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
    const body = await res.json() as { ciphertext: string };
    return decryptJSON<SleepSummary>(passcode, body.ciphertext);
  }

  #transitionToApp(summary: SleepSummary): void {
    const apply = (): void => {
      store.patch({ phase: 'ready', summary, error: null });
    };
    if ('startViewTransition' in document) {
      (document as Document & { startViewTransition: (cb: () => void) => unknown }).startViewTransition(apply);
    } else {
      apply();
    }
  }
}

customElements.define('aramgah-gate', AramgahGate);
